import { useState } from "react";
import DOMPurify from "dompurify";

// ── Pure-JS math helpers ──────────────────────────────────────────────────────

function fmt(n, dp = 3) {
  if (!isFinite(n)) return n > 0 ? "+∞" : "−∞";
  const r = parseFloat(n.toFixed(dp));
  return Object.is(r, -0) ? "0" : String(r);
}

function quadRoots(a, b, c) {
  const D = b * b - 4 * a * c;
  if (D < 0) return { real: false, D };
  if (Math.abs(D) < 1e-10) {
    return { real: true, equal: true, r1: -b / (2 * a), D: 0 };
  }
  const sq = Math.sqrt(D);
  const r1 = (-b - sq) / (2 * a);
  const r2 = (-b + sq) / (2 * a);
  return { real: true, equal: false, r1: Math.min(r1, r2), r2: Math.max(r1, r2), D };
}

function vertexY(a, b, c) { return c - (b * b) / (4 * a); }

// ── Per-type solvers ──────────────────────────────────────────────────────────

function solveSqrtQuad(a, b, c) {
  const D = b * b - 4 * a * c;
  const roots = quadRoots(a, b, c);
  const steps = [];
  steps.push(`Condition for √ to exist: ax² + bx + c ≥ 0`);
  steps.push(`Discriminant D = b² − 4ac = (${b})² − 4(${a})(${c}) = ${fmt(D)}`);

  let domain, range;

  if (!roots.real) {
    if (a > 0) {
      domain = "(−∞, +∞)";
      const minQ = vertexY(a, b, c);
      range = `[${fmt(Math.sqrt(minQ))}, +∞)`;
      steps.push(`D < 0 and a > 0 → quadratic is always positive → Domain = ℝ`);
      steps.push(`Minimum of (${a}x²+${b}x+${c}) = vertex = ${fmt(minQ)}`);
      steps.push(`∴ Range = [√${fmt(minQ)}, +∞) = [${fmt(Math.sqrt(minQ))}, +∞)`);
    } else {
      domain = "∅  (no real x satisfies this)";
      range  = "∅";
      steps.push(`D < 0 and a < 0 → quadratic is always negative → no valid x → Domain = ∅`);
    }
  } else if (roots.equal) {
    const r = roots.r1;
    if (a > 0) {
      domain = "(−∞, +∞)";
      range  = "[0, +∞)";
      steps.push(`D = 0: one repeated root x = ${fmt(r)}`);
      steps.push(`a > 0 → quadratic ≥ 0 for all x (touches 0 at x = ${fmt(r)}) → Domain = ℝ`);
      steps.push(`f(${fmt(r)}) = 0 is the minimum → Range = [0, +∞)`);
    } else {
      domain = `{${fmt(r)}}`;
      range  = "{0}";
      steps.push(`D = 0 and a < 0 → only x = ${fmt(r)} gives expression = 0 → Domain = {${fmt(r)}}`);
      steps.push(`Range = {0}`);
    }
  } else {
    const { r1, r2 } = roots;
    if (a > 0) {
      domain = `(−∞, ${fmt(r1)}] ∪ [${fmt(r2)}, +∞)`;
      range  = "[0, +∞)";
      steps.push(`D > 0, a > 0 → parabola opens UP`);
      steps.push(`Roots: x₁ = ${fmt(r1)},  x₂ = ${fmt(r2)}`);
      steps.push(`Quadratic ≥ 0 outside the roots → Domain: x ≤ ${fmt(r1)} or x ≥ ${fmt(r2)}`);
      steps.push(`Function = 0 at roots, → +∞ as x → ±∞  →  Range = [0, +∞)`);
    } else {
      domain = `[${fmt(r1)}, ${fmt(r2)}]`;
      const maxQ = vertexY(a, b, c);
      range  = `[0, ${fmt(Math.sqrt(Math.max(0, maxQ)))}]`;
      steps.push(`D > 0, a < 0 → parabola opens DOWN`);
      steps.push(`Roots: x₁ = ${fmt(r1)},  x₂ = ${fmt(r2)}`);
      steps.push(`Quadratic ≥ 0 between the roots → Domain: [${fmt(r1)}, ${fmt(r2)}]`);
      steps.push(`Maximum of quadratic at vertex = ${fmt(maxQ)}`);
      steps.push(`∴ Range = [0, √${fmt(maxQ)}] = [0, ${fmt(Math.sqrt(Math.max(0, maxQ)))}]`);
    }
  }
  return { domain, range, steps };
}

function solveLogQuad(a, b, c) {
  const D = b * b - 4 * a * c;
  const roots = quadRoots(a, b, c);
  const steps = [];
  steps.push(`Condition for ln to exist: ax² + bx + c > 0  (strict inequality)`);
  steps.push(`D = b² − 4ac = ${fmt(D)}`);

  let domain, range;

  if (!roots.real) {
    if (a > 0) {
      const minQ = vertexY(a, b, c);
      domain = "(−∞, +∞)";
      range  = `[${fmt(Math.log(minQ))}, +∞)`;
      steps.push(`D < 0, a > 0 → quadratic always positive → Domain = ℝ`);
      steps.push(`Minimum of argument = vertex = ${fmt(minQ)}`);
      steps.push(`Min of ln = ln(${fmt(minQ)}) = ${fmt(Math.log(minQ))}  →  Range = [${fmt(Math.log(minQ))}, +∞)`);
    } else {
      domain = "∅"; range = "∅";
      steps.push(`D < 0, a < 0 → always negative → Domain = ∅`);
    }
  } else if (roots.equal) {
    const r = roots.r1;
    if (a > 0) {
      domain = `(−∞, ${fmt(r)}) ∪ (${fmt(r)}, +∞)`;
      range  = "(−∞, +∞)";
      steps.push(`D = 0: one root x = ${fmt(r)} (quadratic = 0 here → ln undefined)`);
      steps.push(`Exclude x = ${fmt(r)}  →  Domain = ℝ \\ {${fmt(r)}}`);
      steps.push(`ln can reach all real values  →  Range = ℝ`);
    } else {
      domain = "∅"; range = "∅";
    }
  } else {
    const { r1, r2 } = roots;
    if (a > 0) {
      domain = `(−∞, ${fmt(r1)}) ∪ (${fmt(r2)}, +∞)`;
      range  = "(−∞, +∞)";
      steps.push(`D > 0, a > 0: roots x₁ = ${fmt(r1)}, x₂ = ${fmt(r2)}`);
      steps.push(`Quadratic > 0 for x < ${fmt(r1)} or x > ${fmt(r2)}  (open intervals)`);
      steps.push(`As x → roots, argument → 0⁺, ln → −∞; as x → ±∞, ln → +∞  →  Range = ℝ`);
    } else {
      const maxQ = vertexY(a, b, c);
      domain = `(${fmt(r1)}, ${fmt(r2)})`;
      range  = `(−∞, ${fmt(Math.log(maxQ))}]`;
      steps.push(`D > 0, a < 0: roots x₁ = ${fmt(r1)}, x₂ = ${fmt(r2)}`);
      steps.push(`Quadratic > 0 between roots (open interval)`);
      steps.push(`Maximum of argument = vertex = ${fmt(maxQ)}`);
      steps.push(`Max of ln = ln(${fmt(maxQ)}) = ${fmt(Math.log(maxQ))}; ln → −∞ near boundaries`);
      steps.push(`∴ Range = (−∞, ${fmt(Math.log(maxQ))}]`);
    }
  }
  return { domain, range, steps };
}

function solveRational(a, b, c) {
  const D = b * b - 4 * a * c;
  const roots = quadRoots(a, b, c);
  const steps = [];
  steps.push(`Condition: denominator ≠ 0  →  ax² + bx + c ≠ 0`);
  steps.push(`D = ${fmt(D)}`);

  let domain, range;

  if (!roots.real) {
    domain = "(−∞, +∞)";
    const vy = vertexY(a, b, c);
    if (a > 0) {
      range = `(0, ${fmt(1 / vy)}]`;
      steps.push(`D < 0, a > 0 → denominator always positive, never 0 → Domain = ℝ`);
      steps.push(`Min of denominator = vertex = ${fmt(vy)}`);
      steps.push(`Max of f = 1/${fmt(vy)} = ${fmt(1 / vy)};  f → 0⁺ as x → ±∞  →  Range = (0, ${fmt(1 / vy)}]`);
    } else {
      range = `[${fmt(1 / vy)}, 0)`;
      steps.push(`D < 0, a < 0 → denominator always negative → Domain = ℝ`);
      steps.push(`Max of denominator = vertex = ${fmt(vy)}`);
      steps.push(`Min of f = 1/${fmt(vy)} = ${fmt(1 / vy)};  f → 0⁻  →  Range = [${fmt(1 / vy)}, 0)`);
    }
  } else if (roots.equal) {
    const r = roots.r1;
    domain = `(−∞, ${fmt(r)}) ∪ (${fmt(r)}, +∞)`;
    range  = `(−∞, 0) ∪ (0, +∞)  i.e. ℝ \\ {0}`;
    steps.push(`One root at x = ${fmt(r)} → exclude it`);
    steps.push(`f has no zero (numerator = 1), so 0 ∉ range`);
  } else {
    const { r1, r2 } = roots;
    domain = `(−∞, ${fmt(r1)}) ∪ (${fmt(r1)}, ${fmt(r2)}) ∪ (${fmt(r2)}, +∞)`;
    const vy = vertexY(a, b, c);
    if (a > 0) {
      // vertex_y < 0 (since D>0 for a>0)
      range  = `(−∞, ${fmt(1 / vy)}] ∪ (0, +∞)`;
      steps.push(`D > 0, a > 0: roots ${fmt(r1)}, ${fmt(r2)} — exclude both`);
      steps.push(`Between roots: denominator < 0; outside: denominator > 0`);
      steps.push(`f achieves max inside = 1/vertex = ${fmt(1/vy)}  (a negative number)`);
      steps.push(`Range = (−∞, ${fmt(1/vy)}] ∪ (0, +∞)`);
    } else {
      // vertex_y > 0 (since D>0 for a<0)
      range  = `(−∞, 0) ∪ [${fmt(1 / vy)}, +∞)`;
      steps.push(`D > 0, a < 0: roots ${fmt(r1)}, ${fmt(r2)} — exclude both`);
      steps.push(`Between roots: denominator > 0; outside: denominator < 0`);
      steps.push(`f achieves min inside = 1/vertex = ${fmt(1/vy)}`);
      steps.push(`Range = (−∞, 0) ∪ [${fmt(1/vy)}, +∞)`);
    }
  }
  return { domain, range, steps };
}

function solveSinCos(a, b, c) {
  const r = Math.sqrt(a * a + b * b);
  const lo = c - r, hi = c + r;
  const steps = [
    `Standard form: a·sinx + b·cosx = R·sin(x + φ)`,
    `where R = √(a² + b²) = √(${a}² + ${b}²) = √${a*a + b*b} = ${fmt(r)}`,
    `Range of sin = [−1, 1]  →  range of R·sin = [−${fmt(r)}, +${fmt(r)}]`,
    `Adding constant c = ${c}:`,
    `Range = [${fmt(lo)}, ${fmt(hi)}]`,
    `Domain = (−∞, +∞)  [sin and cos are defined for all real x]`,
  ];
  return {
    domain: "(−∞, +∞)",
    range:  `[${fmt(lo)}, ${fmt(hi)}]`,
    steps,
  };
}

function solveQuadratic(a, b, c) {
  const vx = -b / (2 * a);
  const vy = vertexY(a, b, c);
  const steps = [
    `Standard form: f(x) = ${a}x² + (${b})x + (${c})`,
    `Vertex x = −b / (2a) = −(${b}) / (2×${a}) = ${fmt(vx)}`,
    `Vertex value f(${fmt(vx)}) = ${fmt(vy)}`,
  ];
  let range;
  if (a > 0) {
    range = `[${fmt(vy)}, +∞)`;
    steps.push(`a = ${a} > 0 → parabola opens UP → minimum at vertex`);
    steps.push(`Range = [${fmt(vy)}, +∞)`);
  } else if (a < 0) {
    range = `(−∞, ${fmt(vy)}]`;
    steps.push(`a = ${a} < 0 → parabola opens DOWN → maximum at vertex`);
    steps.push(`Range = (−∞, ${fmt(vy)}]`);
  } else {
    range = `{${fmt(c)}}`;
    steps.push(`a = 0 → linear/constant function  →  Range = {${fmt(c)}}`);
  }
  return { domain: "(−∞, +∞)", range, steps };
}

function solveDoubleRoot(a, b) {
  if (a > b) [a, b] = [b, a];
  const span = b - a;
  const fMin = Math.sqrt(span);
  const fMax = Math.sqrt(2 * span);
  const steps = [
    `Two square root conditions:`,
    `① x − ${a} ≥ 0  →  x ≥ ${a}`,
    `② ${b} − x ≥ 0  →  x ≤ ${b}`,
    `Intersection: ${a} ≤ x ≤ ${b}  →  Domain = [${a}, ${b}]`,
    ``,
    `For range, let f = √(x−${a}) + √(${b}−x)`,
    `Square: f² = (x−${a}) + (${b}−x) + 2√[(x−${a})(${b}−x)] = ${span} + 2√[(x−${a})(${b}−x)]`,
    `Term (x−${a})(${b}−x) is maximised at x = ${fmt((a+b)/2)} → value = (${span}/2)² = ${fmt(span*span/4)}`,
    `So f²_max = ${span} + 2×${fmt(span/2)} = ${fmt(2*span)}  →  f_max = √${fmt(2*span)} = ${fmt(fMax)}`,
    `f_min at endpoints: f(${a}) = √${span}, f(${b}) = √${span}  →  f_min = ${fmt(fMin)}`,
    `∴ Range = [${fmt(fMin)}, ${fmt(fMax)}]`,
  ];
  return {
    domain: `[${a}, ${b}]`,
    range:  `[${fmt(fMin)}, ${fmt(fMax)}]`,
    steps,
  };
}

// ── UI config ─────────────────────────────────────────────────────────────────

const TYPES = [
  {
    id: "sqrt",
    label: "√(ax² + bx + c)",
    icon: "√",
    color: "#0EA5E9",
    rgb: "14,165,233",
    tip: "JEE Tip: Set expression ≥ 0 and solve quadratic inequality.",
    fields: [
      { key: "a", label: "a", default: -1 },
      { key: "b", label: "b", default: 0 },
      { key: "c", label: "c", default: 4 },
    ],
    solve: (p) => solveSqrtQuad(+p.a, +p.b, +p.c),
    preview: (p) => `f(x) = √(${p.a}x² + ${p.b}x + ${p.c})`,
  },
  {
    id: "log",
    label: "ln(ax² + bx + c)",
    icon: "ln",
    color: "#a855f7",
    rgb: "168,85,247",
    tip: "JEE Tip: Set expression > 0 (strict) for log/ln.",
    fields: [
      { key: "a", label: "a", default: -1 },
      { key: "b", label: "b", default: 4 },
      { key: "c", label: "c", default: -3 },
    ],
    solve: (p) => solveLogQuad(+p.a, +p.b, +p.c),
    preview: (p) => `f(x) = ln(${p.a}x² + ${p.b}x + ${p.c})`,
  },
  {
    id: "rational",
    label: "1/(ax² + bx + c)",
    icon: "1/x",
    color: "#f97316",
    rgb: "249,115,22",
    tip: "JEE Tip: Exclude all x where denominator = 0.",
    fields: [
      { key: "a", label: "a", default: 1 },
      { key: "b", label: "b", default: -5 },
      { key: "c", label: "c", default: 6 },
    ],
    solve: (p) => solveRational(+p.a, +p.b, +p.c),
    preview: (p) => `f(x) = 1/(${p.a}x² + ${p.b}x + ${p.c})`,
  },
  {
    id: "sincos",
    label: "a·sinx + b·cosx + c",
    icon: "∿",
    color: "#10b981",
    rgb: "16,185,129",
    tip: "JEE Tip: Range = [c − √(a²+b²), c + √(a²+b²)].",
    fields: [
      { key: "a", label: "a (sin coeff)", default: 3 },
      { key: "b", label: "b (cos coeff)", default: 4 },
      { key: "c", label: "c (constant)",  default: 2 },
    ],
    solve: (p) => solveSinCos(+p.a, +p.b, +p.c),
    preview: (p) => `f(x) = ${p.a}sinx + ${p.b}cosx + ${p.c}`,
  },
  {
    id: "quad",
    label: "ax² + bx + c",
    icon: "∪",
    color: "#f43f5e",
    rgb: "244,63,94",
    tip: "JEE Tip: Use vertex = (−b/2a, c − b²/4a) for range.",
    fields: [
      { key: "a", label: "a", default: -2 },
      { key: "b", label: "b", default: 8 },
      { key: "c", label: "c", default: -5 },
    ],
    solve: (p) => solveQuadratic(+p.a, +p.b, +p.c),
    preview: (p) => `f(x) = ${p.a}x² + ${p.b}x + ${p.c}`,
  },
  {
    id: "double",
    label: "√(x−a) + √(b−x)",
    icon: "∬",
    color: "#fbbf24",
    rgb: "251,191,36",
    tip: "JEE Tip: AND the two constraints; range needs f² trick.",
    fields: [
      { key: "a", label: "a (lower)", default: 1 },
      { key: "b", label: "b (upper)", default: 5 },
    ],
    solve: (p) => solveDoubleRoot(+p.a, +p.b),
    preview: (p) => `f(x) = √(x − ${p.a}) + √(${p.b} − x)`,
  },
];

const FORMULA_CARDS = [
  {
    title: "a·sinx + b·cosx",
    formula: "Range = [−R, +R]   where R = √(a² + b²)",
    note: "With constant c: shift by c → [−R+c, R+c]",
    color: "#10b981",
    example: "3sinx + 4cosx → R = 5, Range = [−5, 5]",
  },
  {
    title: "√(ax² + bx + c)",
    formula: "Domain: solve ax² + bx + c ≥ 0",
    note: "Range starts at 0 (or higher if expression has a minimum)",
    color: "#0EA5E9",
    example: "√(4 − x²) → x ∈ [−2, 2], Range = [0, 2]",
  },
  {
    title: "log / ln(f(x))",
    formula: "Domain: solve f(x) > 0  (strict)",
    note: "ln is defined only for strictly positive arguments",
    color: "#a855f7",
    example: "ln(x² − 4x + 3): roots 1 & 3, Domain = (−∞,1)∪(3,∞)",
  },
  {
    title: "Quadratic ax² + bx + c",
    formula: "Vertex y = c − b²/(4a);  x_v = −b/(2a)",
    note: "a > 0: Range = [vertex_y, ∞);  a < 0: Range = (−∞, vertex_y]",
    color: "#f43f5e",
    example: "−2x² + 8x − 5: vertex = 3 → Range = (−∞, 3]",
  },
  {
    title: "√(x − a) + √(b − x)",
    formula: "Domain: [a, b];  Range: [√(b−a), √(2(b−a))]",
    note: "Use f² = (b−a) + 2√((x−a)(b−x)), max by AM-GM at midpoint",
    color: "#fbbf24",
    example: "√(x−1)+√(5−x): Domain=[1,5], Range=[2, 2√2]",
  },
  {
    title: "Rational 1/f(x)",
    formula: "Domain: f(x) ≠ 0  (exclude all zeros of f)",
    note: "Range depends on whether f has 0, 1, or 2 real roots",
    color: "#f97316",
    example: "1/(x²−5x+6): exclude x=2,3 → Domain = ℝ\\{2,3}",
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function MathToolsPage() {
  const [tab, setTab] = useState("calc");
  const [selectedType, setSelectedType] = useState(null);
  const [params, setParams] = useState({});
  const [result, setResult] = useState(null);

  function selectType(t) {
    setSelectedType(t);
    const defaults = {};
    t.fields.forEach(f => { defaults[f.key] = String(f.default); });
    setParams(defaults);
    setResult(null);
  }

  function calculate() {
    try {
      const res = selectedType.solve(params);
      setResult(res);
    } catch (e) {
      setResult({ error: "Could not compute — check inputs." });
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "#070b14",
      color: "#e2e8f0",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      padding: "0 0 60px",
    }}>

      {/* ── Page header ── */}
      <div style={{
        background: "linear-gradient(135deg, rgba(168,85,247,0.12), rgba(14,165,233,0.08))",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "32px 28px 24px",
      }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{
            fontSize: 11, letterSpacing: 3, textTransform: "uppercase",
            background: "linear-gradient(90deg, #f97316, #a855f7)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontWeight: 700, marginBottom: 10,
          }}>JEE Mains · Mathematics</div>
          <h1 style={{
            fontSize: "clamp(22px, 3vw, 34px)", fontWeight: 700,
            margin: "0 0 8px", color: "#f1f5f9", letterSpacing: -0.5,
          }}>Domain &amp; Range Calculator</h1>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0 }}>
            Step-by-step solutions for all standard JEE function types
          </p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 28px" }}>
        <div style={{
          display: "flex", gap: 0,
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          marginBottom: 32, marginTop: 0,
        }}>
          {[
            { id: "calc",     label: "🧮 Calculator" },
            { id: "formulas", label: "📋 Formula Sheet" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "none", border: "none",
              borderBottom: tab === t.id ? "2.5px solid #a855f7" : "2.5px solid transparent",
              color: tab === t.id ? "#e2e8f0" : "rgba(255,255,255,0.4)",
              padding: "14px 22px", cursor: "pointer",
              fontSize: 14, fontWeight: 600, letterSpacing: 0.3,
              transition: "all 0.2s",
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Calculator tab ── */}
        {tab === "calc" && (<>

          {/* Step 1: Choose type */}
          <div style={{ marginBottom: 28 }}>
            <div style={{
              fontSize: 11, color: "rgba(255,255,255,0.35)",
              letterSpacing: 2, textTransform: "uppercase", marginBottom: 14,
            }}>Step 1 — Select function type</div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
              gap: 12,
            }}>
              {TYPES.map(t => (
                <button key={t.id} onClick={() => selectType(t)} style={{
                  background: selectedType?.id === t.id
                    ? `rgba(${t.rgb}, 0.15)` : "rgba(255,255,255,0.03)",
                  border: selectedType?.id === t.id
                    ? `1.5px solid rgba(${t.rgb}, 0.6)` : "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 10, padding: "16px 14px",
                  cursor: "pointer", textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseOver={e => {
                  if (selectedType?.id !== t.id) {
                    e.currentTarget.style.background = `rgba(${t.rgb}, 0.07)`;
                    e.currentTarget.style.borderColor = `rgba(${t.rgb}, 0.3)`;
                  }
                }}
                onMouseOut={e => {
                  if (selectedType?.id !== t.id) {
                    e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  }
                }}
                >
                  <div style={{
                    fontSize: 22, fontFamily: "Georgia, serif",
                    color: t.color, marginBottom: 8, lineHeight: 1,
                  }}>{t.icon}</div>
                  <div style={{
                    fontSize: 12, color: "#e2e8f0", fontWeight: 600,
                    fontFamily: "monospace", letterSpacing: 0.3,
                  }}>{t.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Parameters */}
          {selectedType && (
            <div style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid rgba(${selectedType.rgb}, 0.22)`,
              borderRadius: 12, padding: "24px 22px", marginBottom: 24,
            }}>
              <div style={{
                fontSize: 11, color: "rgba(255,255,255,0.35)",
                letterSpacing: 2, textTransform: "uppercase", marginBottom: 16,
              }}>Step 2 — Enter coefficients</div>

              <div style={{
                fontFamily: "monospace", fontSize: 15,
                color: selectedType.color, marginBottom: 20,
                background: `rgba(${selectedType.rgb}, 0.07)`,
                padding: "10px 16px", borderRadius: 7,
                display: "inline-block",
              }}>{selectedType.preview(params)}</div>

              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
                {selectedType.fields.map(f => (
                  <div key={f.key}>
                    <label style={{
                      display: "block", fontSize: 11,
                      color: "rgba(255,255,255,0.45)", marginBottom: 5,
                      letterSpacing: 1, textTransform: "uppercase",
                    }}>{f.label}</label>
                    <input
                      type="number" step="any"
                      value={params[f.key] ?? f.default}
                      onChange={e => {
                        setParams(p => ({ ...p, [f.key]: e.target.value }));
                        setResult(null);
                      }}
                      style={{
                        width: 90, padding: "9px 12px",
                        background: "rgba(255,255,255,0.06)",
                        border: `1px solid rgba(${selectedType.rgb}, 0.3)`,
                        borderRadius: 7, color: "#f1f5f9",
                        fontSize: 15, fontFamily: "monospace",
                        outline: "none",
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* JEE tip */}
              <div style={{
                background: `rgba(${selectedType.rgb}, 0.06)`,
                border: `1px solid rgba(${selectedType.rgb}, 0.18)`,
                borderRadius: 7, padding: "10px 14px",
                fontSize: 13, color: selectedType.color,
                marginBottom: 18,
              }}>💡 {selectedType.tip}</div>

              <button onClick={calculate} style={{
                background: `linear-gradient(135deg, rgba(${selectedType.rgb},0.85), rgba(${selectedType.rgb},0.6))`,
                border: "none", color: "#fff",
                padding: "12px 32px", borderRadius: 8,
                cursor: "pointer", fontSize: 14, fontWeight: 700,
                letterSpacing: 0.5, boxShadow: `0 4px 18px rgba(${selectedType.rgb},0.3)`,
                transition: "all 0.2s",
              }}
              onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
              onMouseOut={e  => e.currentTarget.style.opacity = "1"}
              >Calculate →</button>
            </div>
          )}

          {/* Step 3: Result */}
          {result && !result.error && (
            <div style={{
              background: "rgba(255,255,255,0.025)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12, padding: "24px 22px",
            }}>
              <div style={{
                fontSize: 11, color: "rgba(255,255,255,0.35)",
                letterSpacing: 2, textTransform: "uppercase", marginBottom: 20,
              }}>Step 3 — Solution</div>

              {/* Domain & Range boxes */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                {[
                  { label: "Domain", value: result.domain, color: "#0EA5E9" },
                  { label: "Range",  value: result.range,  color: "#a855f7" },
                ].map(box => (
                  <div key={box.label} style={{
                    background: `rgba(${box.label === "Domain" ? "14,165,233" : "168,85,247"}, 0.06)`,
                    border: `1px solid rgba(${box.label === "Domain" ? "14,165,233" : "168,85,247"}, 0.22)`,
                    borderRadius: 10, padding: "18px 16px",
                  }}>
                    <div style={{
                      fontSize: 11, color: box.color,
                      letterSpacing: 2, textTransform: "uppercase",
                      marginBottom: 8, fontWeight: 700,
                    }}>{box.label}</div>
                    <div style={{
                      fontSize: 16, color: "#f1f5f9",
                      fontFamily: "monospace", fontWeight: 600,
                      lineHeight: 1.5,
                    }}>{box.value}</div>
                  </div>
                ))}
              </div>

              {/* Step-by-step */}
              <div style={{
                fontSize: 11, color: "rgba(255,255,255,0.35)",
                letterSpacing: 2, textTransform: "uppercase", marginBottom: 14,
              }}>Working</div>
              <ol style={{ margin: 0, padding: "0 0 0 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                {result.steps.filter(Boolean).map((s, i) => (
                  <li key={i} style={{
                    fontSize: 13.5, color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.7, fontFamily: "monospace",
                    background: "rgba(255,255,255,0.02)",
                    padding: "7px 12px", borderRadius: 6,
                    listStyle: s.startsWith("  ") || s === "" ? "none" : undefined,
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.replace(/\*\*(.*?)\*\*/g, "<strong style='color:#f1f5f9'>$1</strong>")) }}
                  />
                ))}
              </ol>
            </div>
          )}

          {result?.error && (
            <div style={{
              background: "rgba(244,63,94,0.07)",
              border: "1px solid rgba(244,63,94,0.25)",
              borderRadius: 10, padding: "16px 18px",
              color: "#f43f5e", fontSize: 14,
            }}>⚠ {result.error}</div>
          )}
        </>)}

        {/* ── Formula Sheet tab ── */}
        {tab === "formulas" && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
            gap: 18,
          }}>
            {FORMULA_CARDS.map(card => (
              <div key={card.title} style={{
                background: "rgba(255,255,255,0.03)",
                border: `1px solid rgba(255,255,255,0.07)`,
                borderRadius: 12, padding: "22px 20px",
                transition: "all 0.2s",
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = `rgba(${hexToRgbStr(card.color)}, 0.06)`;
                e.currentTarget.style.borderColor = `rgba(${hexToRgbStr(card.color)}, 0.3)`;
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
              }}
              >
                <div style={{
                  fontSize: 12, color: card.color, fontWeight: 700,
                  letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
                }}>{card.title}</div>

                <div style={{
                  fontFamily: "monospace", fontSize: 13.5,
                  color: "#f1f5f9", marginBottom: 10, lineHeight: 1.6,
                  background: `rgba(${hexToRgbStr(card.color)}, 0.07)`,
                  padding: "8px 12px", borderRadius: 6,
                }}>{card.formula}</div>

                <div style={{
                  fontSize: 12, color: "rgba(255,255,255,0.45)",
                  lineHeight: 1.6, marginBottom: 12,
                }}>{card.note}</div>

                <div style={{
                  fontSize: 12, color: card.color,
                  fontFamily: "monospace",
                  borderTop: `1px solid rgba(${hexToRgbStr(card.color)}, 0.15)`,
                  paddingTop: 10,
                }}>Example: {card.example}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function hexToRgbStr(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}
